import { AddressIcon6 } from 'app/design/icons/address-icon-6';
import { Enveloppe } from 'app/design/icons/enveloppe';
import { Gift } from 'app/design/icons/gift';
import { OpenLink } from 'app/design/icons/open-link';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { IconCirclePlus, IconUserCircle } from '@tabler/icons-react-native';
import { useNavigation } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { env } from 'app/lib/env';
import { safelyOpenURL } from 'app/lib/navigation';
import { type NeighbourhoodStatusReferral } from 'app/network/statistics';
import { useUser } from 'app/network/user';
import { useTrackNewListingSource } from 'app/network/listing';
import { navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import type { Theme } from 'app/shopify';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import React, { type PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
const theme = createDSTheme();

const AVATAR_SIZE = 36;
const DEFAULT_AVATAR_PATH = '/default/media/avatar.png';

function ReferralAvatar({ url, filled }: { url?: string; filled: boolean }) {
  const isDefaultAvatar = !url || url.includes(DEFAULT_AVATAR_PATH);

  return (
    <View
      style={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {filled && !isDefaultAvatar && url ? (
        <PCImage
          aspectRatio={1}
          cachePolicy="memory-disk"
          height={AVATAR_SIZE}
          source={{ uri: url }}
          style={{ borderRadius: AVATAR_SIZE / 2, backgroundColor: theme.colors['surface.brand.default'] }}
        />
      ) : (
        <IconUserCircle
          strokeWidth={ICON_STROKE_WIDTH}
          color={filled ? theme.colors['surface.brand.default'] : theme.colors['text.secondary']}
          size={AVATAR_SIZE - 4}
        />
      )}
    </View>
  );
}

const BRAND_LINKS = env.IS_PARTAKE
  ? {
      socialLinks: [
        { label: 'Instagram', url: 'https://www.instagram.com/we_partake' },
        { label: 'Facebook', url: 'https://www.facebook.com/wepartake' },
        { label: 'TikTok', url: 'https://www.tiktok.com/@wepartake' },
        { label: 'Youtube', url: 'https://www.youtube.com/@WePartake/shorts' },
      ] as { label: 'Instagram' | 'Facebook' | 'TikTok' | 'Youtube'; url: string }[],
      writeToMayorUrl: 'https://www.we-partake.com/en-us/city-solution/book-a-demo',
    }
  : {
      socialLinks: [
        { label: 'Instagram', url: 'https://www.instagram.com/partageclub' },
        { label: 'Facebook', url: 'https://www.facebook.com/PartageClubApp' },
        { label: 'TikTok', url: 'https://www.tiktok.com/@partageclub' },
        { label: 'Youtube', url: 'https://www.youtube.com/@PartageClub/shorts' },
      ] as { label: 'Instagram' | 'Facebook' | 'TikTok' | 'Youtube'; url: string }[],
      writeToMayorUrl: 'https://www.partage.club/en-ca/city-solution/book-a-demo',
    };

function ImmatureCard({ children, backgroundColor }: PropsWithChildren & { backgroundColor?: keyof Theme['colors'] }) {
  return (
    <Column
      flex={1}
      alignItems="center"
      justifyContent="center"
      gap="spacing.general.x4"
      padding="spacing.general.x5"
      backgroundColor={backgroundColor ?? 'white'}
      borderColor={backgroundColor ? undefined : 'surface.secondary-neutral'}
      borderRadius="radius.lg"
      borderWidth={backgroundColor ? 0 : 1}
    >
      {children}
    </Column>
  );
}

export function DefricheurCard() {
  const { t } = useTranslation('landing-page');
  const navigation = useNavigation();
  const { data: user, isPending: isUserPending } = useUser();
  const trackNewListingSource = useTrackNewListingSource();

  return (
    <ImmatureCard backgroundColor="surface.inverse.tertiary-neutral">
      <Column
        alignItems="center"
        justifyContent="center"
        backgroundColor="surface.brand.subtle"
        borderRadius="radius.xxl"
        height={56}
        width={56}
      >
        <Gift color={theme.colors['text.brand']} width={32} height={32} />
      </Column>
      <Text fontWeight="700" textAlign="center" variant="bodyLarge">
        {t('immature.defricheur.title')}
      </Text>
      <Text textAlign="center" variant="bodyBase">
        <Trans
          i18nKey="immature.defricheur.text"
          ns="landing-page"
          components={{ strong: <Text fontWeight="700" /> }}
        />
      </Text>
      <Button
        onPress={() => {
          trackNewListingSource('landing_page_immature_neighbourhood');
          navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
        }}
        style={{
          width: '100%',
        }}
      >
        <Row
          alignItems="center"
          justifyContent="center"
          gap="spacing.general.x2"
          width="100%"
          backgroundColor="surface.brand.secondary-5.subtle"
          borderRadius="radius.sm"
          paddingHorizontal="spacing.general.x5"
          paddingVertical="spacing.general.x3"
        >
          <Text fontSize={18}>🫴</Text>
          <Text flex={1} fontWeight="700" color="text.primary" variant="bodyBase">
            {t('immature.defricheur.button')}
          </Text>
          <IconCirclePlus strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['text.primary']} />
        </Row>
      </Button>
    </ImmatureCard>
  );
}

export function ReferralInviteCard({ referral }: { referral: NeighbourhoodStatusReferral }) {
  const { t } = useTranslation('landing-page');
  const navigation = useNavigation();

  const total = referral.required;
  const current = referral.current;
  const avatars = referral.avatars ?? [];
  const cycleCurrent = total > 0 ? current % total : 0;

  const goToInvite = () => {
    navigation.navigate('invite', undefined, { pop: true });
  };

  const progress = total > 0 ? (cycleCurrent / total) * 200 : 0;

  return (
    <ImmatureCard>
      <Text fontSize={32}>📣</Text>
      <Text fontWeight="700" textAlign="center" variant="bodyBase">
        <Trans
          i18nKey="immature.referral.title"
          ns="landing-page"
          components={{ strong: <Text fontWeight="700" color="text.brand" /> }}
        />
      </Text>
      <Row alignItems="center" gap="spacing.general.x2">
        {Array.from({ length: total }, (_, i) => i).map((index) => {
          return <ReferralAvatar key={index} url={avatars[index]} filled={index < cycleCurrent} />;
        })}
      </Row>

      <Row height={10} width={200} borderRadius="radius.sm" overflow="hidden" backgroundColor="surface.disabled">
        <Row
          backgroundColor="surface.brand.default"
          borderRadius="radius.sm"
          style={{ height: '100%', width: progress }}
        />
      </Row>

      <Text fontSize={12} color="text.secondary">
        {t('immature.referral.progress', { current: cycleCurrent, total })}
      </Text>
      <Pressable onPress={goToInvite}>
        <Row
          alignItems="center"
          gap="spacing.general.x0,5"
          width="100%"
          backgroundColor="text.brand"
          borderRadius="radius.full"
          paddingHorizontal="spacing.general.x5"
          paddingVertical="spacing.general.x3"
        >
          <AddressIcon6 fillColor="transparent" color={theme.colors['surface.default']} width={28} height={28} />
          <Text fontWeight="700" color="surface.default" variant="bodyBase">
            {t('immature.referral.button')}
          </Text>
        </Row>
      </Pressable>
      <Pressable onPress={goToInvite}>
        <Text color="text.brand" fontWeight="600" variant="bodyBase">
          {t('immature.referral.seeConditions')} →
        </Text>
      </Pressable>
    </ImmatureCard>
  );
}

export function WriteToMayorCard() {
  const { t } = useTranslation('landing-page');

  return (
    <ImmatureCard>
      <Text fontSize={64}>🏫</Text>
      <Text fontWeight="700" textAlign="center" variant="bodyBase">
        {t('immature.writeToMayor.title')}
      </Text>
      <Text textAlign="center" variant="bodyBase" color="text.secondary">
        {t('immature.writeToMayor.text')}
      </Text>
      <Pressable onPress={() => void safelyOpenURL(BRAND_LINKS.writeToMayorUrl)}>
        <Row
          alignItems="center"
          justifyContent="center"
          gap="spacing.general.x2"
          borderColor="surface.inverse.tertiary-neutral"
          borderRadius="radius.full"
          borderWidth={1}
          paddingHorizontal="spacing.general.x5"
          paddingVertical="spacing.general.x3"
        >
          <Enveloppe color={theme.colors['text.primary']} width={24} height={24} />
          <Text fontWeight="600" variant="bodyBase">
            {t('immature.writeToMayor.button')}
          </Text>
          <OpenLink color={theme.colors['text.primary']} width={16} height={16} />
        </Row>
      </Pressable>
    </ImmatureCard>
  );
}

export function SocialMediaCard() {
  const { t } = useTranslation('landing-page');

  const handleSocialLinkPress = (label: 'Facebook' | 'Instagram' | 'TikTok' | 'Youtube', url: string) => {
    track(Event.SocialLinkClicked, { platform: label });
    void safelyOpenURL(url);
  };

  return (
    <ImmatureCard>
      <Text fontSize={64}>🌐</Text>
      <Text fontWeight="700" textAlign="center" variant="bodyBase">
        {t('immature.socialMedia.title')}
      </Text>
      <Text textAlign="center" variant="bodyBase" color="text.secondary">
        {t('immature.socialMedia.text')}
      </Text>
      <Row gap="spacing.general.x4" justifyContent="center" flexWrap="wrap">
        {BRAND_LINKS.socialLinks.map(({ label, url }) => (
          <Pressable
            key={label}
            onPress={() => {
              handleSocialLinkPress(label, url);
            }}
          >
            <Row alignItems="center" gap="spacing.general.x0,5">
              <Text fontWeight="600" variant="bodyBase" color="surface.brand.default">
                {label}
              </Text>
              <OpenLink color={theme.colors['surface.brand.default']} width={12} height={12} />
            </Row>
          </Pressable>
        ))}
      </Row>
    </ImmatureCard>
  );
}
